import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SEO from '../../components/SEO';
import HistoryStats from './components/HistoryStats';
import HistoryFilters from './components/HistoryFilters';
import HistoryTable from './components/HistoryTable';
import HistoryPagination from './components/HistoryPagination';
import EmptyState from './components/EmptyState';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ValidationHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [isLoading, setIsLoading] = useState(false);

  // Mock data for validation history
  const mockHistoryData = [
    {
      id: 'vh-001',
      date: new Date('2024-01-15T10:30:00'),
      startTime: new Date('2024-01-15T10:30:00'),
      type: 'bulk',
      fileName: 'customer_emails_jan_2024.csv',
      totalEmails: 15420,
      validEmails: 12336,
      invalidEmails: 2584,
      unknownEmails: 500,
      successRate: 80,
      status: 'completed',
      duration: '12m 34s',
      syntaxErrors: 1200,
      domainErrors: 984,
      networkErrors: 400
    },
    {
      id: 'vh-002',
      date: new Date('2024-01-14T14:15:00'),
      startTime: new Date('2024-01-14T14:15:00'),
      type: 'single',
      fileName: null,
      totalEmails: 1,
      validEmails: 1,
      invalidEmails: 0,
      unknownEmails: 0,
      successRate: 100,
      status: 'completed',
      duration: '2s',
      syntaxErrors: 0,
      domainErrors: 0,
      networkErrors: 0
    },
    {
      id: 'vh-003',
      date: new Date('2024-01-13T09:45:00'),
      startTime: new Date('2024-01-13T09:45:00'),
      type: 'bulk',
      fileName: 'newsletter_subscribers.txt',
      totalEmails: 8750,
      validEmails: 7000,
      invalidEmails: 1500,
      unknownEmails: 250,
      successRate: 85,
      status: 'completed',
      duration: '8m 15s',
      syntaxErrors: 800,
      domainErrors: 500,
      networkErrors: 200
    },
    {
      id: 'vh-004',
      date: new Date('2024-01-12T16:20:00'),
      startTime: new Date('2024-01-12T16:20:00'),
      type: 'bulk',
      fileName: 'lead_generation_q1.csv',
      totalEmails: 25000,
      validEmails: 18750,
      invalidEmails: 5000,
      unknownEmails: 1250,
      successRate: 75,
      status: 'failed',
      duration: '18m 42s',
      syntaxErrors: 2500,
      domainErrors: 1800,
      networkErrors: 700
    },
    {
      id: 'vh-005',
      date: new Date('2024-01-11T11:10:00'),
      startTime: new Date('2024-01-11T11:10:00'),
      type: 'bulk',
      fileName: 'event_attendees.csv',
      totalEmails: 3200,
      validEmails: 2880,
      invalidEmails: 256,
      unknownEmails: 64,
      successRate: 90,
      status: 'completed',
      duration: '4m 28s',
      syntaxErrors: 150,
      domainErrors: 80,
      networkErrors: 26
    },
    {
      id: 'vh-006',
      date: new Date('2024-01-10T13:30:00'),
      startTime: new Date('2024-01-10T13:30:00'),
      type: 'single',
      fileName: null,
      totalEmails: 1,
      validEmails: 0,
      invalidEmails: 1,
      unknownEmails: 0,
      successRate: 0,
      status: 'completed',
      duration: '1s',
      syntaxErrors: 1,
      domainErrors: 0,
      networkErrors: 0
    },
    {
      id: 'vh-007',
      date: new Date('2024-01-09T15:45:00'),
      startTime: new Date('2024-01-09T15:45:00'),
      type: 'bulk',
      fileName: 'marketing_campaign_dec.csv',
      totalEmails: 12000,
      validEmails: 9600,
      invalidEmails: 1800,
      unknownEmails: 600,
      successRate: 80,
      status: 'processing',
      duration: '0m 0s',
      syntaxErrors: 0,
      domainErrors: 0,
      networkErrors: 0
    }
  ];

  // Mock stats data
  const mockStats = {
    totalSessions: 156,
    sessionsChange: 12,
    totalEmailsValidated: 2847392,
    emailsChange: 8,
    averageSuccessRate: 82,
    successRateChange: 3,
    storageUsed: '2.4 GB',
    storageChange: 15
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = mockHistoryData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        switch (key) {
          case 'type':
            filtered = filtered.filter(item => item.type === value);
            break;
          case 'status':
            filtered = filtered.filter(item => item.status === value);
            break;
          case 'dateFrom':
            filtered = filtered.filter(item => 
              new Date(item.date) >= new Date(value)
            );
            break;
          case 'dateTo':
            filtered = filtered.filter(item => 
              new Date(item.date) <= new Date(value)
            );
            break;
          case 'minEmails':
            filtered = filtered.filter(item => 
              item.totalEmails >= parseInt(value)
            );
            break;
          case 'maxEmails':
            filtered = filtered.filter(item => 
              item.totalEmails <= parseInt(value)
            );
            break;
          default:
            break;
        }
      }
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [searchTerm, filters, sortConfig]);

  // Pagination
  const totalItems = filteredAndSortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectItem = (itemId, isSelected) => {
    setSelectedItems(prev => 
      isSelected 
        ? [...prev, itemId]
        : prev.filter(id => id !== itemId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedItems(isSelected ? paginatedData.map(item => item.id) : []);
  };

  const handleDownload = async (sessionId, format = 'csv') => {
    setIsLoading(true);
    try {
      // Simulate download process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const session = mockHistoryData.find(s => s.id === sessionId);
      if (session) {
        // Create mock download
        const fileName = `validation_results_${sessionId}.${format}`;
        console.log(`Downloading ${fileName}`);
        
        // In a real app, this would trigger an actual file download
        const mockData = format === 'json' 
          ? JSON.stringify(session, null, 2)
          : `Email,Status,Reason\nexample@domain.com,Valid,\ninvalid-email,Invalid,Syntax Error`;
        
        const blob = new Blob([mockData], { 
          type: format === 'json' ? 'application/json' : 'text/csv' 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (sessionId) => {
    if (window.confirm('Are you sure you want to delete this validation session? This action cannot be undone.')) {
      setIsLoading(true);
      try {
        // Simulate delete process
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log(`Deleted session ${sessionId}`);
        // In a real app, this would remove the item from the data source
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.length === 0) return;

    const actionText = action === 'download' ? 'download' : 'delete';
    const confirmMessage = `Are you sure you want to ${actionText} ${selectedItems.length} selected session(s)?`;
    
    if (window.confirm(confirmMessage)) {
      setIsLoading(true);
      try {
        // Simulate bulk action
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (action === 'download') {
          // Simulate bulk download
          selectedItems.forEach(sessionId => {
            handleDownload(sessionId);
          });
        } else if (action === 'delete') {
          // Simulate bulk delete
          console.log(`Bulk deleted sessions: ${selectedItems.join(', ')}`);
        }
        
        setSelectedItems([]);
      } catch (error) {
        console.error(`Bulk ${action} failed:`, error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilters({});
    setSelectedItems([]);
  };

  const hasActiveFilters = searchTerm || Object.keys(filters).length > 0;

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Validation History"
        description="View and manage your email validation history. Track past validations, download results, and analyze your email validation performance over time."
        keywords="email validation history, validation records, email validation tracking, validation analytics, email validation reports, validation history dashboard"
        canonical="https://mailvalidator.octalchip.com/validation-history"
      />
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Breadcrumb />
        
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-text-primary mb-2">
              Validation History
            </h1>
            <p className="text-text-secondary">
              Track and manage all your email validation activities
            </p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={() => handleBulkAction('download')}
              disabled={selectedItems.length === 0 || isLoading}
            >
              Export History
            </Button>
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={() => window.location.reload()}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <HistoryStats stats={mockStats} />

        {/* Filters */}
        <HistoryFilters 
          onSearch={handleSearch}
          onFilter={handleFilter}
        />

        {/* Main Content */}
        {filteredAndSortedData.length === 0 ? (
          <EmptyState 
            hasFilters={hasActiveFilters}
            onClearFilters={clearAllFilters}
          />
        ) : (
          <>
            {/* Results Summary */}
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-text-secondary">
                {totalItems === 1 
                  ? '1 validation session'
                  : `${totalItems.toLocaleString()} validation sessions`
                }
                {hasActiveFilters && ' (filtered)'}
              </div>
              
              {selectedItems.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-text-secondary">
                    {selectedItems.length} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItems([])}
                    iconName="X"
                    iconSize={14}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {/* History Table */}
            <div className="mb-6">
              <HistoryTable
                historyData={paginatedData}
                onDownload={handleDownload}
                onDelete={handleDelete}
                onBulkAction={handleBulkAction}
                selectedItems={selectedItems}
                onSelectItem={handleSelectItem}
                onSelectAll={handleSelectAll}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <HistoryPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300">
            <div className="bg-surface rounded-md p-6 flex items-center space-x-3">
              <Icon name="Loader" size={20} className="animate-spin text-primary" />
              <span className="text-text-primary">Processing...</span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ValidationHistory;